// Página AdminHelpSettings - Configuración de la página de ayuda
// Permite gestionar FAQs y contenido de las secciones de ayuda

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  getHelpSettings, 
  updateHelpSettings,
  addFAQ,
  updateFAQ,
  deleteFAQ,
  reorderFAQs
} from '../../services/helpSettings.service'
import RichTextEditor from '../../components/RichTextEditor'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminHelpSettings.css'

/**
 * Página AdminHelpSettings
 * Gestión completa de la configuración de ayuda
 */
function AdminHelpSettings() {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  
  // Estado para FAQs
  const [editingFAQ, setEditingFAQ] = useState(null)
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: ''
  })
  const [showFAQForm, setShowFAQForm] = useState(false)

  // Cargar configuración
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getHelpSettings()
      setSettings(data)
    } catch (err) {
      console.error('Error cargando configuración:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Guardar configuración general
  const handleSaveSettings = async () => {
    try {
      setSaving(true)
      await updateHelpSettings(settings)
      setNotification({
        type: 'success',
        message: 'Configuración guardada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error guardando configuración:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  // Manejar cambios en secciones de contenido
  const handleContentChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Manejar cambios en datos de contacto
  const handleContactChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      contact: {
        ...(prev.contact || {}),
        [field]: value
      }
    }))
  }

  // Manejar cambios en redes sociales
  const handleSocialMediaChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      contact: {
        ...(prev.contact || {}),
        socialMedia: {
          ...(prev.contact?.socialMedia || {}),
          [platform]: value
        }
      }
    }))
  }

  // Manejar cambios en formulario de FAQ
  const handleFAQFormChange = (field, value) => {
    setFaqForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Agregar nueva FAQ
  const handleAddFAQ = async () => {
    if (!faqForm.question.trim()) {
      setNotification({
        type: 'error',
        message: 'La pregunta es requerida'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    try {
      setSaving(true)
      const order = settings?.faqs?.length || 0
      await addFAQ({
        ...faqForm,
        order
      })
      
      setNotification({
        type: 'success',
        message: 'FAQ agregada correctamente'
      })
      
      resetFAQForm()
      await loadSettings()
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error agregando FAQ:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  // Editar FAQ
  const handleEditFAQ = (faq) => {
    setEditingFAQ(faq)
    setFaqForm({
      question: faq.question,
      answer: faq.answer || ''
    })
    setShowFAQForm(true)
  }

  // Actualizar FAQ
  const handleUpdateFAQ = async () => {
    if (!faqForm.question.trim()) {
      setNotification({
        type: 'error',
        message: 'La pregunta es requerida'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    try {
      setSaving(true)
      await updateFAQ(editingFAQ.id, faqForm)
      
      setNotification({
        type: 'success',
        message: 'FAQ actualizada correctamente'
      })
      
      resetFAQForm()
      await loadSettings()
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error actualizando FAQ:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  // Eliminar FAQ
  const handleDeleteFAQ = async (faqId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta FAQ?')) {
      return
    }

    try {
      setSaving(true)
      await deleteFAQ(faqId)
      
      setNotification({
        type: 'success',
        message: 'FAQ eliminada correctamente'
      })
      
      await loadSettings()
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error eliminando FAQ:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  // Mover FAQ arriba
  const handleMoveFAQUp = async (index) => {
    if (index === 0) return
    
    try {
      const sortedFAQs = [...settings.faqs].sort((a, b) => (a.order || 0) - (b.order || 0))
      const newOrder = [...sortedFAQs]
      ;[newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]]
      
      const faqIds = newOrder.map(faq => faq.id)
      await reorderFAQs(faqIds)
      await loadSettings()
    } catch (err) {
      console.error('Error reordenando FAQ:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Mover FAQ abajo
  const handleMoveFAQDown = async (index) => {
    const sortedFAQs = [...settings.faqs].sort((a, b) => (a.order || 0) - (b.order || 0))
    if (index === sortedFAQs.length - 1) return
    
    try {
      const newOrder = [...sortedFAQs]
      ;[newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]]
      
      const faqIds = newOrder.map(faq => faq.id)
      await reorderFAQs(faqIds)
      await loadSettings()
    } catch (err) {
      console.error('Error reordenando FAQ:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Resetear formulario de FAQ
  const resetFAQForm = () => {
    setFaqForm({ question: '', answer: '' })
    setEditingFAQ(null)
    setShowFAQForm(false)
  }

  // Ordenar FAQs
  const sortedFAQs = settings?.faqs 
    ? [...settings.faqs].sort((a, b) => (a.order || 0) - (b.order || 0))
    : []

  if (loading) {
    return (
      <div className="admin-help-settings-page">
        <div className="loading-state">Cargando configuración...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-help-settings-page">
        <div className="error-state">Error: {error}</div>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="admin-help-settings-page">
        <div className="error-state">No se pudo cargar la configuración</div>
      </div>
    )
  }

  return (
    <div className="admin-help-settings-page">
      <div className="help-settings-header">
        <div>
          <h2>Configuración de Ayuda</h2>
          <p>Gestiona el contenido de la página de ayuda pública</p>
        </div>
        <div className="header-actions">
          <Link to="/admin/configuraciones">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Button 
            variant="primary" 
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Todo'}
          </Button>
        </div>
      </div>

      {/* Sección: Preguntas Frecuentes */}
      <div className="settings-section">
        <div className="section-header">
          <h3>Preguntas Frecuentes (FAQs)</h3>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              resetFAQForm()
              setShowFAQForm(true)
            }}
            disabled={saving}
          >
            + Nueva FAQ
          </Button>
        </div>

        {/* Formulario de FAQ */}
        {showFAQForm && (
          <div className="faq-form-section">
            <h4>{editingFAQ ? 'Editar FAQ' : 'Nueva FAQ'}</h4>
            <div className="form-group">
              <label htmlFor="faq-question">
                Pregunta <span className="required">*</span>
              </label>
              <input
                type="text"
                id="faq-question"
                value={faqForm.question}
                onChange={(e) => handleFAQFormChange('question', e.target.value)}
                placeholder="Ej: ¿Cuál es el tiempo de entrega?"
                disabled={saving}
                className="faq-question-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="faq-answer">Respuesta</label>
              <RichTextEditor
                value={faqForm.answer}
                onChange={(value) => handleFAQFormChange('answer', value)}
                placeholder="Escribe la respuesta aquí..."
              />
            </div>
            <div className="form-actions">
              <Button
                variant="outline"
                onClick={resetFAQForm}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={editingFAQ ? handleUpdateFAQ : handleAddFAQ}
                disabled={saving}
              >
                {editingFAQ ? 'Actualizar' : 'Agregar'} FAQ
              </Button>
            </div>
          </div>
        )}

        {/* Lista de FAQs */}
        <div className="faqs-list">
          {sortedFAQs.length === 0 ? (
            <div className="empty-state">
              <p>No hay FAQs creadas. Agrega la primera FAQ para comenzar.</p>
            </div>
          ) : (
            sortedFAQs.map((faq, index) => (
              <div key={faq.id} className="faq-item-admin">
                <div className="faq-item-header">
                  <div className="faq-order-controls">
                    <button
                      onClick={() => handleMoveFAQUp(index)}
                      disabled={index === 0 || saving}
                      className="order-btn"
                      title="Mover arriba"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveFAQDown(index)}
                      disabled={index === sortedFAQs.length - 1 || saving}
                      className="order-btn"
                      title="Mover abajo"
                    >
                      ↓
                    </button>
                  </div>
                  <div className="faq-item-info">
                    <strong>{faq.question}</strong>
                    <small>Orden: {faq.order || index}</small>
                  </div>
                  <div className="faq-item-actions">
                    <button
                      onClick={() => handleEditFAQ(faq)}
                      className="action-btn edit-btn"
                      disabled={saving}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteFAQ(faq.id)}
                      className="action-btn delete-btn"
                      disabled={saving}
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sección: Información de Envíos */}
      <div className="settings-section">
        <h3>Información de Envíos</h3>
        <RichTextEditor
          value={settings.shippingInfo || ''}
          onChange={(value) => handleContentChange('shippingInfo', value)}
          placeholder="Escribe la información sobre envíos aquí..."
        />
      </div>

      {/* Sección: Cambios y Devoluciones */}
      <div className="settings-section">
        <h3>Cambios y Devoluciones</h3>
        <RichTextEditor
          value={settings.returnsInfo || ''}
          onChange={(value) => handleContentChange('returnsInfo', value)}
          placeholder="Escribe la información sobre cambios y devoluciones aquí..."
        />
      </div>

      {/* Sección: Garantía */}
      <div className="settings-section">
        <h3>Garantía</h3>
        <RichTextEditor
          value={settings.warrantyInfo || ''}
          onChange={(value) => handleContentChange('warrantyInfo', value)}
          placeholder="Escribe la información sobre garantía aquí..."
        />
      </div>

      {/* Sección: Métodos de Pago */}
      <div className="settings-section">
        <h3>Métodos de Pago</h3>
        <RichTextEditor
          value={settings.paymentMethods || ''}
          onChange={(value) => handleContentChange('paymentMethods', value)}
          placeholder="Escribe la información sobre métodos de pago aquí..."
        />
      </div>

      {/* Sección: Información de Contacto */}
      <div className="settings-section">
        <h3>Información de Contacto</h3>
        <p className="section-description">
          Configura la información de contacto que se mostrará en el Footer y la página de ayuda. 
          Solo se mostrarán los campos que tengan contenido.
        </p>

        <div className="contact-form-grid">
          <div className="form-group">
            <label htmlFor="contact-address">
              📍 Dirección
            </label>
            <input
              type="text"
              id="contact-address"
              value={settings.contact?.address || ''}
              onChange={(e) => handleContactChange('address', e.target.value)}
              placeholder="Ej: Av. Corrientes 1234, CABA, Argentina"
              disabled={saving}
              className="contact-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-phone">
              📞 Teléfono
            </label>
            <input
              type="text"
              id="contact-phone"
              value={settings.contact?.phone || ''}
              onChange={(e) => handleContactChange('phone', e.target.value)}
              placeholder="Ej: +54 11 1234-5678"
              disabled={saving}
              className="contact-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-email">
              ✉️ Email
            </label>
            <input
              type="email"
              id="contact-email"
              value={settings.contact?.email || ''}
              onChange={(e) => handleContactChange('email', e.target.value)}
              placeholder="Ej: info@pinohome.com"
              disabled={saving}
              className="contact-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-schedule">
              🕒 Horario de Atención
            </label>
            <input
              type="text"
              id="contact-schedule"
              value={settings.contact?.schedule || ''}
              onChange={(e) => handleContactChange('schedule', e.target.value)}
              placeholder="Ej: Lun - Vie: 9:00 - 19:00hs"
              disabled={saving}
              className="contact-input"
            />
          </div>
        </div>

        <div className="social-media-section">
          <h4 className="social-media-title">Redes Sociales</h4>
          <p className="section-description">
            Configura los enlaces a tus redes sociales. Solo se mostrarán las que tengan URL configurada.
          </p>

          <div className="social-media-grid">
            <div className="form-group">
              <label htmlFor="social-facebook">
                <span className="social-icon">📘</span> Facebook
              </label>
              <input
                type="url"
                id="social-facebook"
                value={settings.contact?.socialMedia?.facebook || ''}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                placeholder="https://facebook.com/tu-pagina"
                disabled={saving}
                className="contact-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="social-instagram">
                <span className="social-icon">📷</span> Instagram
              </label>
              <input
                type="url"
                id="social-instagram"
                value={settings.contact?.socialMedia?.instagram || ''}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                placeholder="https://instagram.com/tu-cuenta"
                disabled={saving}
                className="contact-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="social-whatsapp">
                <span className="social-icon">💬</span> WhatsApp
              </label>
              <input
                type="text"
                id="social-whatsapp"
                value={settings.contact?.socialMedia?.whatsapp || ''}
                onChange={(e) => handleSocialMediaChange('whatsapp', e.target.value)}
                placeholder="5491112345678 (sin + ni espacios, con código de país)"
                disabled={saving}
                className="contact-input"
              />
              <small className="form-hint">
                Solo números, con código de país. Ejemplo: 5491112345678 (Argentina)
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de guardar al final */}
      <div className="settings-footer">
        <Button
          variant="primary"
          size="large"
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar Todos los Cambios'}
        </Button>
      </div>

      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default AdminHelpSettings

